import {
	useRef,
	useState,
	useEffect,
	useImperativeHandle,
	forwardRef,
	ReactNode,
	MouseEvent,
	ImgHTMLAttributes,
} from "react";

import { useWindowDimensions } from "@/hooks/useWindowDimensions";

export type Point = {
	content: string;
	x: number;
	y: number;
};

export type ViewpointMethods = {
	createPoint: (content: string) => void;
	cancelOrDestroyPoint: () => void;
	previewPoint: (content: string) => void;
	unpreviewPoint: () => void;
};

type ViewpointProps = ImgHTMLAttributes<HTMLImageElement> & {
	points: Point[];
	onChangePoints: (points: Point[]) => void;
	onPointClick?: (point: Point, index: number) => void;
	renderPointContent?: (point: Point) => ReactNode;
};

export const Viewpoint = forwardRef<ViewpointMethods, ViewpointProps>(
	({ points, onChangePoints, onPointClick, renderPointContent, ...rest }, ref) => {
		const viewRef = useRef<HTMLImageElement | null>(null);

		const x = useRef<number>();
		const y = useRef<number>();
		const pointIndex = useRef<number>();

		const [placeholderPoint, setPlaceholderPoint] = useState<Point>();
		const [bounds, setBounds] = useState<DOMRect>();

		const { width, height } = useWindowDimensions();

		function handleClick(event: MouseEvent<HTMLImageElement>) {
			const viewBounds = viewRef.current?.getBoundingClientRect();

			setBounds(viewBounds);

			if (!viewBounds) {
				return;
			}

			const clientX = event.clientX - viewBounds.x;
			const clientY = event.clientY - viewBounds.y;

			x.current = clientX;
			y.current = clientY;
		}

		useEffect(() => {
			if (!width || !height) {
				return;
			}

			const viewBounds = viewRef.current?.getBoundingClientRect();
			setBounds(viewBounds);
		}, [width, height]);

		function getPoint(content: string) {
			const clientX = x.current;
			const clientY = y.current;

			if (!clientX || !clientY) {
				alert("Não foi possível adicionar o ponto.");
				return;
			}

			return { content, x: clientX, y: clientY };
		}

		function cleanUp() {
			setPlaceholderPoint(undefined);
			x.current = y.current = pointIndex.current = undefined;
		}

		function handleSwitchPoint(content: string) {
			if (pointIndex.current !== undefined) {
				const newPoints = points.map((point, index) => {
					if (index === pointIndex.current) {
						return { content, x: point.x, y: point.y };
					}

					return point;
				});

				onChangePoints(newPoints);
				cleanUp();

				return;
			}

			const newPoint = getPoint(content);

			cleanUp();

			if (!newPoint) {
				return;
			}

			onChangePoints([...points, newPoint]);
		}

		function handleEditIcon(index: number) {
			pointIndex.current = index;

			const viewBounds = viewRef.current?.getBoundingClientRect();
			setBounds(viewBounds);
		}

		function handlePreviewPoint(content: string) {
			if (pointIndex.current !== undefined) {
				return;
			}

			const newPoint = getPoint(content);

			if (!newPoint) {
				return;
			}

			setPlaceholderPoint(newPoint);
		}

		function handleStopPreviewPoint() {
			setPlaceholderPoint(undefined);
		}

		function handleCancelSwitchPoint() {
			if (pointIndex.current !== undefined) {
				const newPoints = points.filter((_, index) => index !== pointIndex.current);

				onChangePoints(newPoints);
			}

			cleanUp();
		}

		useImperativeHandle(ref, () => ({
			createPoint(content) {
				handleSwitchPoint(content);
			},
			cancelOrDestroyPoint() {
				handleCancelSwitchPoint();
			},
			previewPoint(content) {
				handlePreviewPoint(content);
			},
			unpreviewPoint() {
				handleStopPreviewPoint();
			},
		}));

		const VERTICAL_OFFSET = 10;
		const HORIZONTAL_OFFSET = 4;

		return (
			<div style={{ position: "relative" }}>
				{points.map(
					(point, index) =>
						bounds && (
							<span
								key={index}
								onClick={() => {
									handleEditIcon(index);
									onPointClick && onPointClick(point, index);
								}}
								style={{
									position: "absolute",
									top: Number(point.y) - VERTICAL_OFFSET,
									left: Number(point.x) - HORIZONTAL_OFFSET,
									cursor: "pointer",
								}}
							>
								{renderPointContent ? renderPointContent(point) : point.content}
							</span>
						)
				)}

				{placeholderPoint && bounds && (
					<span
						style={{
							position: "absolute",
							top: placeholderPoint.y - VERTICAL_OFFSET,
							left: placeholderPoint.x - HORIZONTAL_OFFSET,
							color: "#808080",
						}}
					>
						{renderPointContent
							? renderPointContent(placeholderPoint)
							: placeholderPoint.content}
					</span>
				)}

				<img
					alt="Imagem"
					ref={viewRef}
					{...rest}
					onClick={(event) => {
						handleClick(event);
						rest.onClick && rest.onClick(event);
					}}
				/>
			</div>
		);
	}
);
